import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Graficele ecranului Endpoint Performance, desenate de mana in SVG.
 *
 * Ecranul vechi (public/js/inventoree/endpoint_dashboard.js) foloseste Chart.js 2:
 * o gogoasa cu gaura de 80% si scrisul in mijloc, un bar vertical colorat pe
 * praguri, un horizontalBar STIVUIT pe patru serii si doua bare de milisecunde.
 * v4 le desena pe toate cu aceeasi lista plata de bare - de aici "nu se vad
 * graficele ca pe cec-master": nu lipseau, dar patru forme diferite ajunsesera
 * una singura, fara culori pe praguri, fara stive si fara gogoasa.
 *
 * De ce nu o biblioteca: portalul intra in audit bancar si amCharts a fost scos
 * cu doua probe incuiate. Un grafic cu o serie sau patru nu are nevoie de
 * biblioteca; are nevoie sa arate la fel. Aceeasi socoteala ca la
 * ui/SimpleLineChart.tsx.
 *
 * Culorile si formulele sunt COPIATE din endpoint_dashboard.js, nu alese aici:
 * praguri 70/50 pe scorul de performanta, 8 ore ca zi de lucru, taierea la 100%.
 */

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif'
const AXIS = '#666'
const GRID = 'rgba(0,0,0,0.1)'

export const ENDPOINT_COLORS = {
  processor: '#F0AD4E',
  ram: '#5CB85C',
  swap: '#337AB7',
  services: '#5BC0DE',
} as const

/** Zi de lucru, ca in ecranul vechi: 8 ore. */
export const WORK_HOURS = 8

/** ((ore / 8) * 100), taiat la 100 - formula scorului din endpoint_dashboard.js. */
export function scorePercent(hours: number): number {
  const pct = Number(((hours / WORK_HOURS) * 100).toFixed(1))
  return pct > 100 ? 100 : pct
}

/** Pragurile de culoare ale scorului: rosu de la 70, portocaliu de la 50, verde sub. */
export function scoreColor(percent: number): string {
  if (percent >= 70) return '#D9534F'
  if (percent >= 50) return '#F0AD4E'
  return '#33C733'
}

/** Minutele scrise cum le scria tooltipul vechi: peste o ora in "h", sub ea in "m". */
export function minutesLabel(minutes: number): string {
  return minutes > 59 ? `${Math.round(minutes / 60)}h` : `${minutes}m`
}

/**
 * Capatul de sus al axei, rotunjit ca la Chart.js: 1/2/5 x 10^n, vreo cinci trepte.
 */
export function niceScale(max: number, ticks = 5): { top: number; step: number } {
  if (!(max > 0)) return { top: 1, step: 1 }
  const rough = max / ticks
  const power = Math.pow(10, Math.floor(Math.log10(rough)))
  const rest = rough / power
  const step = (rest > 5 ? 10 : rest > 2 ? 5 : rest > 1 ? 2 : 1) * power
  return { top: Math.ceil(max / step) * step, step }
}

/**
 * Latimea panoului, masurata.
 *
 * Cand masurarea da zero - la prima trecere, sau intr-un jsdom care nu asaza
 * nimic - se deseneaza tot, pe o latime de rezerva. Un grafic care asteapta o
 * masuratoare ca sa apara e, pentru cine se uita la ecran, un grafic lipsa.
 */
function useWidth(fallback = 300) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [box, width > 0 ? width : fallback] as const
}

interface Tip {
  x: number
  y: number
  title: string
  body: string
}

/**
 * Cand nu e nimic de desenat se scrie DE CE, nu se lasa o rama goala.
 * Placile de pe tablourile de modul fac la fel de ieri.
 */
export function ChartEmpty({ children }: { children: ReactNode }) {
  return (
    <div
      className="endpoint-chart-empty text-muted"
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        fontSize: 12,
        padding: '0 10px',
        whiteSpace: 'normal',
      }}
    >
      {children}
    </div>
  )
}

function Tooltip({ tip, width }: { tip: Tip | null; width: number }) {
  if (!tip) return null
  const left = Math.min(Math.max(4, tip.x + 10), Math.max(4, width - 150))
  return (
    <div
      className="endpoint-chart-tip"
      style={{
        position: 'absolute',
        left,
        top: Math.max(2, tip.y - 38),
        background: 'rgba(0,0,0,0.8)',
        color: '#fff',
        borderRadius: 6,
        padding: '4px 8px',
        fontFamily: FONT,
        fontSize: 12,
        lineHeight: '16px',
        pointerEvents: 'none',
        maxWidth: 160,
        zIndex: 5,
      }}
    >
      <div style={{ fontWeight: 'bold' }}>{tip.title}</div>
      <div>{tip.body}</div>
    </div>
  )
}

/** Cat de lunga poate fi o eticheta inclinata inainte sa iasa din desen. */
const MAX_TICK_CHARS = 24

/** Inaltimea cea mai mica a unei bare: o bara de zero tot trebuie sa se vada. */
const MIN_BAR = 2

function tick(label: string): string {
  return label.length > MAX_TICK_CHARS ? `${label.slice(0, MAX_TICK_CHARS - 1)}…` : label
}

export interface BarItem {
  label: string
  value: number
  color: string
  /** Ce scrie in balon, daca nu chiar valoarea. */
  tip?: string
}

/**
 * Bar vertical, ca `type: 'bar'` din Chart.js 2: grila orizontala, axa de la
 * zero, etichetele inclinate cand nu incap.
 */
export function VerticalBarChart({ items, height = 260 }: { items: BarItem[]; height?: number }) {
  const [box, width] = useWidth()
  const [hover, setHover] = useState<number | null>(null)

  /*
   * Etichetele stau inclinate la 45 de grade, deci cer jos cat sunt de lungi
   * impartit la radical din doi. Fara socoteala asta, un nume de aparat lipit
   * de o adresa iese din desen si se vede taiat - Chart.js micsoreaza graficul
   * ca sa incapa eticheta, si asta face si randul de mai jos.
   */
  const charPx = 5.6
  const longest = Math.max(0, ...items.map((i) => Math.min(i.label.length, MAX_TICK_CHARS))) * charPx
  const pad = { top: 12, right: 10, bottom: Math.min(120, Math.round(22 + longest * 0.71)), left: 42 }
  const plotLeft = pad.left
  const plotRight = Math.max(plotLeft + 1, width - pad.right)
  const plotTop = pad.top
  const plotBottom = height - pad.bottom
  const plotW = Math.max(1, plotRight - plotLeft)
  const plotH = Math.max(1, plotBottom - plotTop)
  const { top, step } = niceScale(Math.max(...items.map((i) => i.value), 0))
  const band = plotW / Math.max(1, items.length)
  const barW = Math.min(60, band * 0.72)
  const y = (v: number) => plotBottom - (v / top) * plotH
  const gridlines: number[] = []
  for (let v = 0; v <= top + 1e-9; v += step) gridlines.push(Number(v.toFixed(6)))

  return (
    <div ref={box} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img">
          {gridlines.map((v) => (
            <g key={v}>
              <line x1={plotLeft} x2={plotRight} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
              <text x={plotLeft - 6} y={y(v) + 4} textAnchor="end" style={{ fill: AXIS, fontSize: 11, fontFamily: FONT }}>
                {v}
              </text>
            </g>
          ))}
          {items.map((item, i) => {
            const cx = plotLeft + band * i + band / 2
            /*
             * O bara de ZERO se vede, ca un prag lipit de axa.
             *
             * Fara `MIN_BAR`, un rand de zero se desena inalt de zero pixeli:
             * numele lui statea sub axa, si deasupra nu era nimic - adica exact
             * ce se citeste ca "graficul e stricat", nu ca "aici e zero". Se
             * intampla de cand Productivitee isi arata si masinile care au
             * lucrat o zi intreaga cu 0% (21 septembrie 2026), dar regula e
             * buna oriunde: daca numele a intrat in desen, are dreptul la un
             * semn.
             */
            const h = Math.max(MIN_BAR, plotBottom - y(item.value))
            return (
              <g key={item.label + i}>
                <rect
                  x={cx - barW / 2}
                  y={plotBottom - h}
                  width={barW}
                  height={h}
                  fill={item.color}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                />
                <text
                  x={cx}
                  y={plotBottom + 8}
                  textAnchor="end"
                  transform={`rotate(-45 ${cx} ${plotBottom + 8})`}
                  style={{ fill: AXIS, fontSize: 10, fontFamily: FONT }}
                >
                  {tick(item.label)}
                </text>
              </g>
            )
          })}
        </svg>
      )}
      <Tooltip
        width={width}
        tip={
          hover === null || !items[hover]
            ? null
            : {
                x: plotLeft + band * hover + band / 2,
                y: y(items[hover].value),
                title: items[hover].label,
                body: items[hover].tip ?? String(items[hover].value),
              }
        }
      />
    </div>
  )
}

export interface StackedRow {
  label: string
  parts: { key: string; value: number; color: string }[]
}

/**
 * `type: 'horizontalBar'` cu `stacked: true` pe amandoua axele - patru serii
 * (procesor / ram / swap / servicii) una peste alta pe acelasi aparat.
 */
export function StackedBarChart({ rows, height = 260 }: { rows: StackedRow[]; height?: number }) {
  const [box, width] = useWidth()
  const [hover, setHover] = useState<{ row: number; part: number } | null>(null)

  const pad = { top: 8, right: 14, bottom: 24, left: 170 }
  const plotLeft = pad.left
  const plotRight = Math.max(plotLeft + 1, width - pad.right)
  const plotTop = pad.top
  const plotBottom = Math.max(plotTop + 1, height - pad.bottom)
  const plotW = Math.max(1, plotRight - plotLeft)
  const plotH = Math.max(1, plotBottom - plotTop)
  const totals = rows.map((r) => r.parts.reduce((s, p) => s + p.value, 0))
  const { top, step } = niceScale(Math.max(...totals, 0))
  const band = plotH / Math.max(1, rows.length)
  const barH = Math.min(28, band * 0.72)
  const x = (v: number) => plotLeft + (v / top) * plotW
  const gridlines: number[] = []
  for (let v = 0; v <= top + 1e-9; v += step) gridlines.push(Number(v.toFixed(6)))

  return (
    <div ref={box} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img">
          {gridlines.map((v) => (
            <g key={v}>
              <line x1={x(v)} x2={x(v)} y1={plotTop} y2={plotBottom} stroke={GRID} strokeWidth={1} />
              <text x={x(v)} y={plotBottom + 14} textAnchor="middle" style={{ fill: AXIS, fontSize: 11, fontFamily: FONT }}>
                {v}
              </text>
            </g>
          ))}
          {rows.map((row, i) => {
            const cy = plotTop + band * i + band / 2
            let run = 0
            return (
              <g key={row.label + i}>
                <text x={plotLeft - 6} y={cy + 4} textAnchor="end" style={{ fill: AXIS, fontSize: 10, fontFamily: FONT }}>
                  {row.label.length > 30 ? `${row.label.slice(0, 29)}…` : row.label}
                </text>
                {row.parts.map((part, j) => {
                  const x0 = x(run)
                  run += part.value
                  const w = Math.max(0, x(run) - x0)
                  if (w <= 0) return null
                  return (
                    <rect
                      key={part.key}
                      x={x0}
                      y={cy - barH / 2}
                      width={w}
                      height={barH}
                      fill={part.color}
                      onMouseEnter={() => setHover({ row: i, part: j })}
                      onMouseLeave={() => setHover(null)}
                    />
                  )
                })}
              </g>
            )
          })}
        </svg>
      )}
      <Tooltip
        width={width}
        tip={(() => {
          if (!hover) return null
          const row = rows[hover.row]
          const part = row?.parts[hover.part]
          if (!row || !part) return null
          let run = 0
          for (let j = 0; j < hover.part; j++) run += row.parts[j].value
          return {
            x: x(run + part.value / 2),
            y: plotTop + band * hover.row + band / 2,
            title: part.key,
            body: minutesLabel(part.value),
          }
        })()}
      />
    </div>
  )
}

export interface Slice {
  label: string
  value: number
  color: string
}

/**
 * Gogoasa cu gaura de 80% si scrisul in mijloc - `cutoutPercentage: 80` plus
 * pluginul `elements.center` inregistrat in capul lui endpoint_dashboard.js.
 */
export function DoughnutChart({
  slices,
  centerText,
  centerColor = '#D9534F',
  height = 260,
}: {
  slices: Slice[]
  centerText: string
  centerColor?: string
  height?: number
}) {
  const [box, width] = useWidth()
  const [hover, setHover] = useState<number | null>(null)

  const legendH = 30
  const total = slices.reduce((s, item) => s + item.value, 0)
  const cx = width / 2
  const cy = legendH + (height - legendH) / 2
  const outer = Math.max(4, Math.min(width, height - legendH) / 2 - 8)
  const inner = outer * 0.8

  let angle = -Math.PI / 2
  const arcs = slices.map((slice) => {
    const sweep = total > 0 ? (slice.value / total) * Math.PI * 2 : 0
    const start = angle
    angle += sweep
    return { slice, start, end: angle, sweep }
  })

  const point = (r: number, a: number) => `${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`
  const ring = (start: number, end: number) => {
    const large = end - start > Math.PI ? 1 : 0
    if (end - start >= Math.PI * 2 - 1e-6) {
      /* O singura felie: doua semicercuri, ca arcul intreg nu se poate desena dintr-o bucata. */
      const mid = start + Math.PI
      return (
        `M${point(outer, start)} A${outer} ${outer} 0 0 1 ${point(outer, mid)}` +
        ` A${outer} ${outer} 0 0 1 ${point(outer, start)}` +
        ` M${point(inner, start)} A${inner} ${inner} 0 0 0 ${point(inner, mid)}` +
        ` A${inner} ${inner} 0 0 0 ${point(inner, start)} Z`
      )
    }
    return (
      `M${point(outer, start)} A${outer} ${outer} 0 ${large} 1 ${point(outer, end)}` +
      ` L${point(inner, end)} A${inner} ${inner} 0 ${large} 0 ${point(inner, start)} Z`
    )
  }

  /* Scrisul din mijloc creste cat sa incapa in gaura, ca in pluginul vechi. */
  const fontSize = Math.max(10, Math.min(((inner * 2 * 0.85) / Math.max(1, centerText.length)) * 1.6, inner))

  return (
    <div ref={box} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img">
          <g>
            {slices.map((slice, i) => (
              <g key={slice.label} transform={`translate(${(i % 2) * (width / 2) + 6}, ${Math.floor(i / 2) * 14})`}>
                <rect x={0} y={2} width={16} height={8} fill={slice.color} />
                <text x={20} y={10} style={{ fill: AXIS, fontSize: 10, fontFamily: FONT }}>
                  {slice.label}
                </text>
              </g>
            ))}
          </g>
          {arcs.map((arc, i) =>
            arc.sweep > 0 ? (
              <path
                key={arc.slice.label}
                d={ring(arc.start, arc.end)}
                fill={arc.slice.color}
                stroke="#fff"
                strokeWidth={2}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            ) : null,
          )}
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fill: centerColor, fontSize, fontFamily: `Quicksand, ${FONT}` }}
          >
            {centerText}
          </text>
        </svg>
      )}
      <Tooltip
        width={width}
        tip={
          hover === null || !arcs[hover]
            ? null
            : {
                x: cx + ((outer + inner) / 2) * Math.cos((arcs[hover].start + arcs[hover].end) / 2),
                y: cy + ((outer + inner) / 2) * Math.sin((arcs[hover].start + arcs[hover].end) / 2),
                title: arcs[hover].slice.label,
                body: String(arcs[hover].slice.value),
              }
        }
      />
    </div>
  )
}
